import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Link, Paper, Stack, Typography } from '@mui/material';
import { AuthService } from '../../services/auth.service';
import { MaintenanceDashboardService } from '../../services/maintenance.service';
import { MntTechnicianDashboard, mntLabel } from '../../interfaces/maintenance';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { WO_PRIORITY_COLOR, WO_STATUS_COLOR } from '../maintenance/woCommon';
import { DashList, fmtShortDate, machineLabel } from '../maintenance/MntDashBits';
import MyBreakdownsDashSection from '../maintenance/MyBreakdownsDashSection';
import { DashSection } from './personal/DashSection';
import StatTile from './charts/StatTile';
import UserDashboardView from './UserDashboardView';
import { useDashboardAccess } from './useDashboardAccess';

const fmtInt = (n: number) => Math.round(n).toLocaleString('en-US');

/**
 * Maintenance technician home: my own work only. The server returns work orders assigned to (or worked by)
 * the caller and no cost or value field, so nothing here can show another person's work or any money.
 */
const TechnicianDashboard: React.FC = () => {
    const { enabledModules, ready } = useDashboardAccess();
    const moduleOff = ready && !enabledModules.has('maintenance');
    const canRecord = usePermission(PERMISSIONS.MAINTENANCE_READING_RECORD);
    const [data, setData] = useState<MntTechnicianDashboard | null>(null);
    const [failed, setFailed] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) { setFailed(true); setLoading(false); return; }
        try {
            setData(await MaintenanceDashboardService.technician(institutionId));
            setFailed(false);
        } catch {
            setFailed(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (!moduleOff) load(); }, [load, moduleOff]);

    if (moduleOff) return <UserDashboardView />;

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
    }

    const k = data?.kpis;

    return (
        <Box data-help-page="maint.techDash.page">
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 2 }}>
                <Typography variant="h5" fontWeight="bold">My Maintenance Work</Typography>
                <Stack data-help-section="maint.techDash.shortcuts" direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button component={RouterLink} to="/maintenance/work-orders" size="small" variant="contained">My work orders</Button>
                    <Button component={RouterLink} to="/maintenance/pm-due" size="small" variant="outlined">PM due</Button>
                    {canRecord && <Button component={RouterLink} to="/maintenance/readings" size="small" variant="outlined">Utility log</Button>}
                </Stack>
            </Stack>

            {failed && !data && <Alert severity="warning" sx={{ mb: 2 }} data-help-section="maint.techDash.notice">Your maintenance work is unavailable right now.</Alert>}

            {data && k && (
                <>
                    <Paper variant="outlined" sx={{ p: 1, mb: 2 }} data-help-section="maint.techDash.kpis">
                        <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap' }} data-testid="tech-kpi-strip">
                            <StatTile data-help="maint.techDash.tile.myOpen" label="My open work" value={fmtInt(k.myOpen)} />
                            <StatTile data-help="maint.techDash.tile.inProgress" label="In progress" value={fmtInt(k.inProgress)} tone={k.inProgress > 0 ? 'warning' : 'default'} />
                            <StatTile data-help="maint.techDash.tile.waitingParts" label="Waiting for parts" value={fmtInt(k.waitingParts)} tone={k.waitingParts > 0 ? 'warning' : 'default'} />
                            <StatTile data-help="maint.techDash.tile.completedToday" label="Completed today" value={fmtInt(k.completedToday)} tone={k.completedToday > 0 ? 'good' : 'default'} />
                            {canRecord && <StatTile data-help="maint.techDash.tile.readingsDue" label="Readings due today" value={fmtInt(k.readingsDueToday)} tone={k.readingsDueToday > 0 ? 'warning' : 'default'} />}
                        </Stack>
                    </Paper>

                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}>
                        <DashSection title={`My work (${data.myWork.total})`} subtitle="Most urgent first" data-help-section="maint.techDash.myWork">
                            <DashList block={data.myWork} empty="You have no open work orders." testId="tech-my-work" keyOf={r => r.id}
                                render={r => (
                                    <>
                                        <Link component={RouterLink} to={`/maintenance/work-orders/${r.id}`} underline="hover" sx={{ fontWeight: 600 }}>{r.woNo}</Link>
                                        <Typography variant="body2" color="text.secondary">{r.title}</Typography>
                                        <Box sx={{ flex: 1 }} />
                                        {r.machineDown && <Chip size="small" color="error" label="Machine down" />}
                                        <Chip size="small" label={mntLabel(r.priority)} color={WO_PRIORITY_COLOR[r.priority as keyof typeof WO_PRIORITY_COLOR] ?? 'default'} />
                                        <Chip size="small" label={mntLabel(r.status)} color={WO_STATUS_COLOR[r.status as keyof typeof WO_STATUS_COLOR] ?? 'default'} />
                                    </>
                                )} />
                        </DashSection>
                        <DashSection title={`PM due, assigned to me (${data.pmDueMine.total})`} subtitle="Preventive work, soonest due first" data-help-section="maint.techDash.pmDue">
                            <DashList block={data.pmDueMine} empty="No preventive work is assigned to you." testId="tech-pm-due" keyOf={r => r.id}
                                render={r => (
                                    <>
                                        <Link component={RouterLink} to={`/maintenance/work-orders/${r.id}`} underline="hover" sx={{ fontWeight: 600 }}>{r.woNo}</Link>
                                        <Typography variant="body2" color="text.secondary">{machineLabel(r.equipmentCode, r.equipmentName, r.locationName)}</Typography>
                                        <Box sx={{ flex: 1 }} />
                                        <Typography variant="caption" color="text.secondary">Due {fmtShortDate(r.pmDueDate)}</Typography>
                                    </>
                                )} />
                        </DashSection>
                        {canRecord && (
                            <DashSection title={`Readings to record (${data.readingsToRecord.total})`} subtitle="Utility meters with no reading yet today" data-help-section="maint.techDash.readings">
                                <DashList block={data.readingsToRecord} empty="All readings are in for today." testId="tech-readings" keyOf={r => r.meterId}
                                    render={r => (
                                        <>
                                            <Link component={RouterLink} to="/maintenance/readings" underline="hover" sx={{ fontWeight: 600 }}>{r.meterName || r.meterCode}</Link>
                                            <Typography variant="body2" color="text.secondary">{machineLabel(r.equipmentCode, r.equipmentName, r.locationName)}</Typography>
                                            <Box sx={{ flex: 1 }} />
                                            {r.unit && <Typography variant="caption" color="text.secondary">{r.unit}</Typography>}
                                        </>
                                    )} />
                            </DashSection>
                        )}
                        <DashSection title="Recently completed" subtitle="My last finished jobs" data-help-section="maint.techDash.recent">
                            <DashList block={data.recentCompletions} empty="Nothing completed yet." testId="tech-recent" keyOf={r => r.id}
                                render={r => (
                                    <>
                                        <Link component={RouterLink} to={`/maintenance/work-orders/${r.id}`} underline="hover" sx={{ fontWeight: 600 }}>{r.woNo}</Link>
                                        <Typography variant="body2" color="text.secondary">{r.title}</Typography>
                                        <Box sx={{ flex: 1 }} />
                                        <Typography variant="caption" color="text.secondary">{fmtShortDate(r.completedAt)}</Typography>
                                    </>
                                )} />
                        </DashSection>
                    </Box>
                </>
            )}

            <MyBreakdownsDashSection />
        </Box>
    );
};

export default TechnicianDashboard;
